import { Box, Chip } from '@mui/material';

import { USER_APPROVE_STATUS, USER_QUALIFY_STATUS } from '@/config/constants';
import { useProductStatus } from '@/hooks/useProductStatus';
import {
  isProductContractRatified,
  productHasLinkedContract,
} from '@/utils/productContractHelpers';
import {
  Product,
  PRODUCT_APPROVE_STATUS,
  PRODUCT_QUALIFY_STATUS,
} from '@/types/ProductType';

type ProductBadgesProps = {
  /** The loan officer approved this home's loan application. */
  loanApproved?: boolean;
  product?: Product;
};

function ContractPhaseChip({ product }: { product: Product }) {
  if (!productHasLinkedContract(product)) return null;

  if (isProductContractRatified(product)) {
    return (
      <Chip
        label="Ratified"
        size="small"
        color="success"
        variant="filled"
        icon={<i className="fa-light fa-file-signature" />}
      />
    );
  }

  // A populated contract knows better than the product's contractStarted flag,
  // which stays set (and read "Negotiating") once signing has begun.
  const linked =
    typeof product.contract === 'object' ? product.contract : undefined;
  if (
    linked?.contractStatus === 'awaiting_signatures' ||
    linked?.contractStatus === 'awaiting_seller_signature'
  ) {
    return (
      <Chip
        label="Awaiting signatures"
        size="small"
        color="info"
        variant="filled"
        icon={<i className="fa-light fa-file-signature" />}
      />
    );
  }

  if (product.contractStarted) {
    return (
      <Chip
        label="Negotiating"
        size="small"
        color="warning"
        variant="filled"
        icon={<i className="fa-light fa-comments" />}
      />
    );
  }

  if (product.contractInProgress) {
    return (
      <Chip
        label="In progress"
        size="small"
        color="secondary"
        variant="filled"
        icon={<i className="fa-light fa-circle-notch" />}
      />
    );
  }

  return (
    <Chip
      label="Contract draft"
      size="small"
      color="default"
      variant="outlined"
      icon={<i className="fa-light fa-file-lines" />}
    />
  );
}

export const ProductBadges = ({
  product,
  loanApproved = false,
}: ProductBadgesProps) => {
  if (!product) return null;
  const { qualifyStatus, approveStatus } = useProductStatus(product);

  const contractPhaseChip = <ContractPhaseChip product={product} />;

  const refinanceChip =
    product.transactionMode === 'refinance' ? (
      <Chip
        label="Refinance"
        size="small"
        color="info"
        variant="filled"
        icon={<i className="fa-light fa-arrows-rotate" />}
      />
    ) : null;

  if (!product.user) {
    const extras = [refinanceChip, contractPhaseChip].filter(Boolean);
    if (!extras.length) return null;
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          justifyContent: 'flex-end',
        }}
      >
        {extras}
      </Box>
    );
  }
  const isUserApproved =
    product.user.approveStatus === USER_APPROVE_STATUS.APPROVED &&
    product?.user?.approvalAmount;
  // An approved loan file outranks the older profile-level qualify/approve
  // flags, which the loan officer's decision doesn't touch.
  if (loanApproved) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          justifyContent: 'flex-end',
        }}
      >
        {refinanceChip}
        {contractPhaseChip}
        <Chip
          label="Approved"
          size="small"
          color="success"
          icon={<i className="fa-light fa-circle-check" />}
        />
      </Box>
    );
  }
  const isUserQualified =
    product?.user?.qualifyStatus === USER_QUALIFY_STATUS.QUALIFY;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        justifyContent: 'flex-end',
      }}
    >
      {refinanceChip}
      {contractPhaseChip}
      {!isUserApproved &&
        isUserQualified &&
        (qualifyStatus === PRODUCT_QUALIFY_STATUS.QUALIFIED ? (
          <Chip
            label="Qualified"
            size="small"
            color="success"
            icon={<i className="fa-light fa-circle-o" />}
          />
        ) : (
          <Chip
            label="Unqualified"
            size="small"
            color="error"
            icon={<i className="fa-light fa-circle-x" />}
          />
        ))}
      {isUserApproved &&
        (approveStatus === PRODUCT_APPROVE_STATUS.APPROVED ? (
          <Chip
            label="Approved"
            size="small"
            color="success"
            icon={<i className="fa-light fa-circle-o" />}
          />
        ) : (
          <Chip
            label="Not Approved"
            size="small"
            color="error"
            icon={<i className="fa-light fa-circle-x" />}
          />
        ))}
    </Box>
  );
};
